import { useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';

/**
 * Manages Android hardware back button and browser history integration.
 * Builds a modal/overlay stack so pressing "back" closes the topmost layer
 * instead of exiting the app.
 */
interface BackNavigationState {
  isAuthOpen: boolean;
  isProfileOpen: boolean;
  showUpgrade: boolean;
  showAdmin: boolean;
  showAddModal: boolean;
  showNotifications: boolean;
  showPackingList: boolean;
  showMyTracks: boolean;
  showTesterApplication: boolean;
  showMobileSheet: boolean;
  mobileView: 'map' | 'list';
  selectedRouteId: string | null;
}

interface BackNavigationActions {
  setIsAuthOpen: (v: boolean) => void;
  setIsProfileOpen: (v: boolean) => void;
  setShowUpgrade: (v: boolean) => void;
  setShowAdmin: (v: boolean | ((prev: boolean) => boolean)) => void;
  setShowAddModal: (v: boolean) => void;
  setShowNotifications: (v: boolean) => void;
  setShowPackingList: (v: boolean) => void;
  setShowMyTracks: (v: boolean) => void;
  setShowTesterApplication: (v: boolean) => void;
  setShowMobileSheet: (v: boolean) => void;
  onSetMobileView: (v: 'map' | 'list') => void;
  onCloseRouteDetails?: () => void;
}

export function useBackNavigation(
  state: BackNavigationState,
  actions: BackNavigationActions,
  onExitWarning?: () => void,
) {
  const stateRef = useRef(state);
  stateRef.current = state;

  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  const handleBack = useCallback((): boolean => {
    const s = stateRef.current;
    const a = actionsRef.current;

    // Close modals/overlays in priority order (topmost first)
    if (s.showAdmin) { a.setShowAdmin(false); return true; }
    if (s.isAuthOpen) { a.setIsAuthOpen(false); return true; }
    if (s.isProfileOpen) { a.setIsProfileOpen(false); return true; }
    if (s.showUpgrade) { a.setShowUpgrade(false); return true; }
    if (s.showAddModal) { a.setShowAddModal(false); return true; }
    if (s.showNotifications) { a.setShowNotifications(false); return true; }
    if (s.showPackingList) { a.setShowPackingList(false); return true; }
    if (s.showMyTracks) { a.setShowMyTracks(false); return true; }
    if (s.showTesterApplication) { a.setShowTesterApplication(false); return true; }

    // Close route detail view
    if (s.selectedRouteId && a.onCloseRouteDetails) { a.onCloseRouteDetails(); return true; }

    // Close mobile spot sheet
    if (s.showMobileSheet) { a.setShowMobileSheet(false); return true; }

    // Close sidebar (return to map)
    if (s.mobileView === 'list') { a.onSetMobileView('map'); return true; }

    // Nothing to close — allow default behavior (exit app)
    return false;
  }, []);

  // Capacitor native back button (Android) — dobbelttrykk for å avslutte
  const lastBackPressRef = useRef(0);
  const exitToastShownRef = useRef(false);
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const listenerPromise = CapacitorApp.addListener('backButton', (ev) => {
      const handled = handleBack();
      if (!handled && !ev.canGoBack) {
        const now = Date.now();
        if (now - lastBackPressRef.current < 2000) {
          CapacitorApp.exitApp();
        } else {
          lastBackPressRef.current = now;
          if (!exitToastShownRef.current) {
            exitToastShownRef.current = true;
            try { navigator.vibrate?.(100); } catch {}
            onExitWarning?.();
            setTimeout(() => { exitToastShownRef.current = false; }, 2000);
          }
        }
      }
    });
    return () => { listenerPromise.then(l => l.remove()); };
  }, [handleBack]);

  // Browser history popstate (web/PWA)
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    const onPopState = (e: PopStateEvent) => {
      const handled = handleBack();
      if (handled) {
        // Re-push state to keep history entry for next back press
        window.history.pushState({ fricamp: true }, '');
      }
    };

    // Push an initial state entry
    window.history.pushState({ fricamp: true }, '');
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [handleBack]);
}
